import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    const saved = localStorage.getItem('knowfi_user');
    if (!saved) { navigate('/login'); return; }
    const local = JSON.parse(saved);
    setUser(local); // show immediately from cache
    // Fetch fresh data from server
    axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${local.token}` } })
      .then(res => {
        const fresh = { ...local, ...res.data };
        setUser(fresh);
        localStorage.setItem('knowfi_user', JSON.stringify(fresh));
      })
      .catch(() => {}); // silently keep cached data if server unreachable
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('knowfi_user');
    navigate('/');
  };

  const setPw = (k, v) => setPwForm(f => ({ ...f, [k]: v }));

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) { setPwError('All fields are required.'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError('New passwords do not match.'); return; }
    if (pwForm.newPw.length < 6) { setPwError('New password must be at least 6 characters.'); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        token: user.token,
        currentPassword: pwForm.current,
        newPassword: pwForm.newPw,
      });
      setPwSuccess('✅ Password updated successfully.');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to update password.');
    }
    setPwLoading(false);
  };

  if (!user) return null;

  const isPro = user.plan === 'pro';
  const initials = user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';
  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;

  return (
    <div style={s.page}>
      <SEO title="My Account" description="Manage your KnowFi account." path="/account" />
      <div style={s.glow} />

      <div style={s.container}>

        {/* Hero card */}
        <div style={s.heroCard}>
          <div style={s.avatar}>{initials}</div>
          <div style={s.heroInfo}>
            <h1 style={s.heroName}>{user.name}</h1>
            <div style={s.heroMeta}>
              {user.email && <span style={s.heroBadge}>✉️ {user.email}</span>}
              {memberSince && <span style={s.heroBadge}>📅 Member since {memberSince}</span>}
            </div>
          </div>
          <div style={{ ...s.planBadge, ...(isPro ? s.planBadgePro : s.planBadgeFree) }}>
            {isPro ? '⭐ Pro' : 'Free'}
          </div>
        </div>

        {/* Plan banner */}
        {isPro ? (
          <div style={s.proBanner}>
            <div>
              <div style={s.proBannerTitle}>⭐ KnowFi Pro — Active</div>
              <div style={s.proBannerSub}>Full access to all features. Thanks for supporting KnowFi.</div>
            </div>
          </div>
        ) : (
          <div style={s.freeBanner}>
            <div>
              <div style={s.freeBannerTitle}>You're on the Free Plan</div>
              <div style={s.freeBannerSub}>5 AI uses/day · Limited market data · Basic tools</div>
            </div>
            <Link to="/pricing" style={s.upgradeBtn}>Upgrade to Pro →</Link>
          </div>
        )}

        {/* Tabs */}
        <div style={s.tabs}>
          {['profile', 'security'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }}>
              {tab === 'profile' ? '👤 Profile' : '🔒 Security'}
            </button>
          ))}
        </div>

        {/* Profile tab */}
        {activeTab === 'profile' && (
          <div style={s.card}>
            <h2 style={s.cardTitle}>Profile Info</h2>
            <div style={s.infoGrid}>
              <InfoRow label="Full Name" value={user.name} />
              <InfoRow label="Email" value={user.email || '—'} />
              <InfoRow label="Plan" value={isPro ? 'KnowFi Pro ⭐' : 'Free'} />
              <InfoRow label="Member Since" value={memberSince || '—'} last />
            </div>
            <div style={s.logoutSection}>
              <button onClick={logout} style={s.logoutBtn}>Log Out of This Device</button>
            </div>
          </div>
        )}

        {/* Security tab */}
        {activeTab === 'security' && (
          <div style={s.card}>
            <h2 style={s.cardTitle}>Change Password</h2>
            <p style={s.cardSub}>Use a strong password you don't use anywhere else.</p>
            <form onSubmit={changePassword} style={s.form}>
              <Field label="Current Password" type="password" placeholder="••••••••"
                value={pwForm.current} onChange={e => setPw('current', e.target.value)} />
              <Field label="New Password" type="password" placeholder="Min 6 characters"
                value={pwForm.newPw} onChange={e => setPw('newPw', e.target.value)} />
              <Field label="Confirm New Password" type="password" placeholder="••••••••"
                value={pwForm.confirm} onChange={e => setPw('confirm', e.target.value)} />
              {pwError && <div style={s.error}>{pwError}</div>}
              {pwSuccess && <div style={s.success}>{pwSuccess}</div>}
              <button type="submit" style={{ ...s.submitBtn, ...(pwLoading ? s.submitDisabled : {}) }} disabled={pwLoading}>
                {pwLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
}

function InfoRow({ label, value, last }) {
  return (
    <div style={{ ...rowS.row, ...(last ? {} : rowS.rowBorder) }}>
      <span style={rowS.label}>{label}</span>
      <span style={rowS.value}>{value}</span>
    </div>
  );
}

function Field({ label, type, placeholder, value, onChange }) {
  return (
    <div style={fieldS.wrap}>
      <label style={fieldS.label}>{label}</label>
      <input style={fieldS.input} type={type} placeholder={placeholder} value={value} onChange={onChange} />
    </div>
  );
}

const rowS = {
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0' },
  rowBorder: { borderBottom: '1px solid #1e3a52' },
  label: { color: '#64748b', fontSize: '14px' },
  value: { color: '#f8fafc', fontSize: '14px', fontWeight: '500' },
};

const fieldS = {
  wrap: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' },
  input: { background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f8fafc', padding: '12px 14px', fontSize: '15px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
};

const s = {
  page: { background: '#0f172a', minHeight: '100vh', color: '#f8fafc', padding: '48px 20px 80px', position: 'relative' },
  glow: { position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)', width: '900px', height: '500px', background: 'radial-gradient(ellipse at center top, rgba(56,189,248,0.1) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 },
  container: { maxWidth: '620px', margin: '0 auto', position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: '16px' },

  // Hero
  heroCard: { background: 'linear-gradient(135deg, #1e293b 0%, #0f2540 100%)', border: '1px solid #334155', borderRadius: '20px', padding: '28px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' },
  avatar: { width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '800', color: '#fff', flexShrink: 0 },
  heroInfo: { flex: 1, minWidth: '160px' },
  heroName: { fontSize: '22px', fontWeight: '800', margin: '0 0 8px', letterSpacing: '-0.3px' },
  heroMeta: { display: 'flex', flexDirection: 'column', gap: '4px' },
  heroBadge: { color: '#94a3b8', fontSize: '13px' },
  planBadge: { padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' },
  planBadgePro: { background: 'rgba(234,179,8,0.15)', color: '#fbbf24', border: '1px solid rgba(234,179,8,0.3)' },
  planBadgeFree: { background: 'rgba(100,116,139,0.15)', color: '#94a3b8', border: '1px solid #334155' },

  // Banners
  proBanner: { background: 'linear-gradient(135deg, rgba(234,179,8,0.08), rgba(234,179,8,0.03))', border: '1px solid rgba(234,179,8,0.25)', borderRadius: '14px', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  proBannerTitle: { fontSize: '16px', fontWeight: '700', color: '#fbbf24', marginBottom: '4px' },
  proBannerSub: { color: '#94a3b8', fontSize: '13px' },
  freeBanner: { background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '14px', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' },
  freeBannerTitle: { fontSize: '15px', fontWeight: '700', marginBottom: '4px' },
  freeBannerSub: { color: '#64748b', fontSize: '13px' },
  upgradeBtn: { background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', padding: '10px 20px', borderRadius: '10px', fontWeight: '700', fontSize: '14px', textDecoration: 'none', whiteSpace: 'nowrap' },

  // Tabs
  tabs: { display: 'flex', gap: '8px' },
  tab: { flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #334155', background: '#1e293b', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' },
  tabActive: { background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8' },

  // Card
  card: { background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '28px' },
  cardTitle: { fontSize: '16px', fontWeight: '700', margin: '0 0 4px' },
  cardSub: { color: '#64748b', fontSize: '14px', margin: '0 0 20px' },
  infoGrid: { marginBottom: '24px' },

  // Form
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  error: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', color: '#f87171', fontSize: '14px' },
  success: { background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '8px', padding: '10px 14px', color: '#4ade80', fontSize: '14px' },
  submitBtn: { background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', border: 'none', padding: '13px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' },
  submitDisabled: { opacity: 0.6, cursor: 'not-allowed' },

  // Logout
  logoutSection: { borderTop: '1px solid #334155', paddingTop: '20px' },
  logoutBtn: { background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', padding: '10px 20px', borderRadius: '10px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },
};

export default Account;
